import api from '@/lib/axios';

export interface InvoiceItem {
  id?: string;
  productId?: string;
  satProductKey: string;
  satUnitKey: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxBase: number;
  taxRate: number;
  taxAmount: number;
  subtotal: number;
  total: number;
}

export interface Invoice {
  id: string;
  folio: string; // Internal folio
  uuid?: string; // SAT UUID
  companyId: string;
  customerId?: string;
  customerName: string;
  customerRfc: string;
  taxRegime: string;
  taxZipCode: string;
  cfdiUse: string; // Uso de CFDI
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  status: 'PENDING' | 'STAMPED' | 'CANCELLED' | 'FAILED';
  saleId?: string | null;
  kind?: 'SALE' | 'GLOBAL'; // SALE individual | GLOBAL consolidada diaria
  salesCount?: number | null; // Nº de operaciones consolidadas (solo GLOBAL)
  pdfUrl?: string;
  xmlUrl?: string;
  errorDetails?: string;
  createdAt: string;
  stampedAt?: string;
  cancelledAt?: string;
  items?: InvoiceItem[];
}

export interface CreateInvoiceDto {
  customerId?: string;
  customerName: string;
  customerRfc: string;
  taxRegime: string;
  taxZipCode: string;
  cfdiUse: string;
  paymentForm: string;
  paymentMethod: string;
  saleId?: string;
  email?: string;
}

class InvoicesService {
  async findAll(companyId: string, search?: string): Promise<Invoice[]> {
    const response = await api.get<Invoice[]>('/invoices', {
      params: { companyId, search },
      headers: { 'x-bypass-local': 'true' }
    });
    return response.data;
  }

  async findById(id: string): Promise<Invoice> {
    const response = await api.get<Invoice>(`/invoices/${id}`, {
      headers: { 'x-bypass-local': 'true' }
    });
    return response.data;
  }

  async create(companyId: string, branchId: string, data: CreateInvoiceDto): Promise<Invoice> {
    const response = await api.post<Invoice>('/invoices', {
      ...data,
      companyId,
    }, { headers: { 'x-bypass-local': 'true' } });
    return response.data;
  }

  /** Emite la factura global diaria consolidando las ventas a Público en General del día/branch */
  async createGlobal(companyId: string, branchId: string, date?: string): Promise<Invoice> {
    const response = await api.post<Invoice>('/invoices/global', {
      companyId,
      branchId,
      date: date || undefined,
    }, { headers: { 'x-bypass-local': 'true' } });
    return response.data;
  }

  async cancel(id: string, reason: string): Promise<Invoice> {
    const response = await api.post<Invoice>(`/invoices/${id}/cancel`, {
      reason,
    }, { headers: { 'x-bypass-local': 'true' } });
    return response.data;
  }

  async download(id: string, type: 'xml' | 'pdf', filename: string): Promise<void> {
    const response = await api.get(`/invoices/${id}/download/${type}`, {
      responseType: 'blob',
      headers: { 'x-bypass-local': 'true' }
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const invoicesService = new InvoicesService();

